/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "../logging/Log";
import { WebSocketSerializer } from "../websocket/WebSocketSerializer";
import { PushNotificationModelImpl } from "./PushNotificationModelImpl";
import { WebSocketNotificationParser } from "./WebSocketNotificationParser";
import { PushNotificationSource, PushNotificationType } from "./types";
import type { IPushNotificationSource, TPushNotification } from "./types";
import type { WebSocketManager } from "../websocket/WebSocketManager";
import type { IWebSocketMessage } from "../websocket/types";

/**
 * Callback invoked with each accepted notification.
 */
type PushNotificationMessageHandler = (
  data: TPushNotification,
  source: PushNotificationSource
) => void;

/**
 * Stage 1 — receive EAS and other push notifications over the WebSocket.
 *
 * Registers itself against WebSocketManager's handler registry for every
 * RequestType in HANDLED_OPERATIONS. Each inbound request is acknowledged
 * immediately and the feature work is deferred, so the MRPC response frame
 * is never held up by downstream processing.
 *
 * Must be constructed and started per WebSocketManager instance:
 * WebSocketManager.destroy() clears the handler registry, so a source
 * registered against a destroyed manager stops receiving notifications.
 *
 * FUTURE SCOPE: to support another RequestType, append it to
 * HANDLED_OPERATIONS here, add the parse and route branches, and append it
 * to `supportedLocalOperation` in WebSocketManager.handleBodyConfigSearch()
 * so the server begins sending it.
 */
export class WebSocketPushNotificationSource implements IPushNotificationSource {
  private static readonly TAG: string = "WebSocketPushNotificationSource";

  /** RequestTypes this source subscribes to. */
  private static readonly HANDLED_OPERATIONS: readonly string[] = [
    PushNotificationType.EMERGENCY_ALERT_DISPLAY
  ];

  /**
   * Acknowledgement returned for every inbound request, whether or not the
   * payload could be parsed.
   *
   * A content-level failure is not worth an MRPC error: the frame was
   * received and correlated, and an error response only invites the server
   * to retry a payload this client can never consume. WebSocketManager
   * discards this object when the request did not ask for a response
   * (`ResponseCount` absent or `none`), so no branching is needed here.
   */
  private static readonly ACK_SUCCESS: Record<string, unknown> = {
    type: "success"
  };

  private readonly mManager: WebSocketManager;
  private readonly mMessageHandler: PushNotificationMessageHandler;

  /** Pending deferred-processing timers, cleared on destroy(). */
  private readonly mDeferredTimers: Set<ReturnType<typeof setTimeout>> =
    new Set<ReturnType<typeof setTimeout>>();

  /** Whether the handlers are currently registered. */
  private mIsListening: boolean = false;

  /**
   * @param manager - The manager whose handler registry to register against
   * @param messageHandler - Receives each accepted notification. Defaults to
   *   PushNotificationModelImpl; injectable for tests.
   */
  constructor(
    manager: WebSocketManager,
    messageHandler?: PushNotificationMessageHandler
  ) {
    this.mManager = manager;

    if (messageHandler === undefined) {
      this.mMessageHandler = (
        data: TPushNotification,
        source: PushNotificationSource
      ): void => {
        PushNotificationModelImpl.getInstance().handleMessage(data, source);
      };
    } else {
      this.mMessageHandler = messageHandler;
    }
  }

  /**
   * Register a handler for every RequestType in HANDLED_OPERATIONS.
   * Idempotent.
   */
  public startListening(): void {
    if (this.mIsListening) {
      Log.d(
        WebSocketPushNotificationSource.TAG,
        "startListening(): Already listening"
      );
      return;
    }

    for (const operation of WebSocketPushNotificationSource.HANDLED_OPERATIONS) {
      this.mManager.registerHandler(
        operation,
        (message: IWebSocketMessage): Promise<Record<string, unknown>> => {
          return this.handleRequest(message);
        }
      );
    }

    this.mIsListening = true;
    Log.i(
      WebSocketPushNotificationSource.TAG,
      `startListening(): Registered ${WebSocketPushNotificationSource.HANDLED_OPERATIONS.join(", ")}`
    );
  }

  /**
   * Deregister every handler registered by startListening(). Idempotent.
   */
  public stopListening(): void {
    if (!this.mIsListening) {
      return;
    }

    for (const operation of WebSocketPushNotificationSource.HANDLED_OPERATIONS) {
      this.mManager.deregisterHandler(operation);
    }

    this.mIsListening = false;
    Log.i(
      WebSocketPushNotificationSource.TAG,
      "stopListening(): Deregistered handlers"
    );
  }

  /**
   * Deregister handlers and abandon any deferred processing. Idempotent.
   */
  public destroy(): void {
    this.stopListening();

    for (const timer of this.mDeferredTimers) {
      clearTimeout(timer);
    }
    const abandoned: number = this.mDeferredTimers.size;
    this.mDeferredTimers.clear();

    Log.d(
      WebSocketPushNotificationSource.TAG,
      `destroy(): Abandoned ${abandoned} deferred notifications`
    );
  }

  /**
   * Handle one inbound MRPC request.
   *
   * Never throws and never resolves to null: WebSocketManager turns either
   * into an `internalError` response. Every outcome acknowledges the frame
   * and the failure detail goes to the log instead.
   *
   * @param message - The inbound request
   * @returns The acknowledgement to send back
   */
  private async handleRequest(
    message: IWebSocketMessage
  ): Promise<Record<string, unknown>> {
    const requestType: string =
      message.operationName !== null ? message.operationName : "";

    Log.i(
      WebSocketPushNotificationSource.TAG,
      `handleRequest(): Received requestType=${requestType}, rpcId=${message.id}, originRequestId=${message.originRequestId}, bodyLen=${message.body.length}`
    );

    let payload: Record<string, unknown>;
    try {
      payload = WebSocketSerializer.deserialize(message);
    } catch (error: unknown) {
      Log.w(
        WebSocketPushNotificationSource.TAG,
        `handleRequest(): Failed to deserialize body for requestType=${requestType}, rpcId=${message.id}`,
        error
      );
      return WebSocketPushNotificationSource.ACK_SUCCESS;
    }

    const notification: TPushNotification | null =
      WebSocketNotificationParser.toPushNotification(
        requestType,
        payload,
        message.headers,
        PushNotificationSource.WEBSOCKET
      );

    if (notification === null) {
      Log.w(
        WebSocketPushNotificationSource.TAG,
        `handleRequest(): Nothing parsed for requestType=${requestType}, rpcId=${message.id}`
      );
      return WebSocketPushNotificationSource.ACK_SUCCESS;
    }

    this.deferFeatureProcessing(notification);

    return WebSocketPushNotificationSource.ACK_SUCCESS;
  }

  /**
   * Hand the notification to the message handler on a later macrotask.
   *
   * setTimeout, not a microtask: WebSocketManager awaits this handler and
   * only then writes the response frame, so a microtask queued here would
   * drain before the acknowledgement was sent — defeating the point of
   * acknowledging first.
   *
   * @param data - The parsed notification
   */
  private deferFeatureProcessing(data: TPushNotification): void {
    const timer: ReturnType<typeof setTimeout> = setTimeout((): void => {
      this.mDeferredTimers.delete(timer);
      try {
        this.mMessageHandler(data, PushNotificationSource.WEBSOCKET);
      } catch (error: unknown) {
        Log.e(
          WebSocketPushNotificationSource.TAG,
          `deferFeatureProcessing(): Handler failed for originRequestId=${data.originRequestId}`,
          error
        );
      }
    }, 0);

    this.mDeferredTimers.add(timer);
  }
}
